import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import jsQR from 'jsqr';
import { useConsumerT } from '../translations/landingConsumer';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConsumer } from '../../contexts/ConsumerContext';
import { useParcelScanContext } from '../pages/ParcelScan/ParcelScanPageContext';
import ConsumerBackButton from '../components/ConsumerBackButton';
import { getParcelInfo } from '../../services';
import { buildBrandFromApiData, buildParcelFromApiData } from '../hooks/useParcelLoader';
import AlertSnackbar from '../components/AlertSnackbar';

/**
 * ScanScreenContainer — écran 3 (viewfinder QR + fallback saisie manuelle).
 * Scan réel via getUserMedia + jsQR. QR URL attendue : mobp.io/:parcelID
 * Flux : scan → fetch unique → erreur = bouton Retry / succès = navigate
 */

export default function ScanScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dispatch: consumerDispatch } = useConsumer();
  const { fetchAlert, alertMessage, dispatch: parcelScanPageDispatch } = useParcelScanContext();
  const [manual, setManual] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanError, setScanError] = useState(false);
  const videoRef = useRef(null);
  const blurVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false);
  const isFetchingRef = useRef(false);

  function handleAlertClose() {
    parcelScanPageDispatch({ type: 'PAGE_PARCEL_SCAN_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } });
  }

  function normalizeParcelTagCode(rawCode) {
    const cleanCode = rawCode.trim().toUpperCase();
    if (cleanCode.startsWith('MP') && cleanCode.length === 8) return cleanCode;
    if (cleanCode.length === 6) return `MP${cleanCode}`;
    return cleanCode;
  }

  function numericPairToChar(pair) {
    if (pair === '00') return '_';
    if (pair === '99') return '-';
    if (pair.startsWith('8')) return pair[1];
    return String.fromCharCode(parseInt(pair) + 'A'.charCodeAt(0) - 1);
  }

  function graiToBase64(graiString) {
    const numericPart = graiString.substring(1, 15);
    const remainingPart = graiString.substring(15);
    const chars = [];
    for (let i = 0; i < numericPart.length; i += 2) {
      chars.push(numericPairToChar(numericPart.substring(i, i + 2)));
    }
    return chars.join('') + remainingPart;
  }

  function decodeUUID(b64) {
    const clean = b64.replace(/!/g, '_');
    const bin = atob(clean.replace(/-/g, '+').replace(/_/g, '/'));
    const hex = Array.from(bin, c => ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('');
    return hex.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
  }

  function extractParcelID(qrData) {
    if (!qrData) return null;

    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('[scan] QR raw:', JSON.stringify(qrData));
    }

    // Format GS1-128 : paramètre ?8003=
    try {
      const sections = qrData.split('/');
      if (sections.length >= 6 && sections[3] === '01') {
        const { searchParams } = new URL(qrData);
        const grai = searchParams.get('8003');
        if (grai) return decodeUUID(graiToBase64(grai));
      }
    } catch (_) {}

    // Format Legacy : UUID direct (mobiuspack.com, ship-dev.mobiuspack.io, ship.mobiuspack.io)
    let match = qrData.match(/mobiuspack(?:\.com|\.io)\/(?:[^/]+\/)*([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
    if (match) return match[1];

    // Format Compact : mobp.io + base64URL (22–24 chars)
    match = qrData.match(/mobp\.io\/([0-9a-zA-Z_!-]{22,24})/);
    if (match) {
      try { return decodeUUID(match[1]); } catch (_) {}
    }

    return null;
  }

  function freezeCamera() {
    scanningRef.current = false;
    if (videoRef.current) videoRef.current.pause();
  }

  function resumeCamera() {
    if (videoRef.current) videoRef.current.play();
  }

  function stopCamera() {
    scanningRef.current = false;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    if (blurVideoRef.current) {
      blurVideoRef.current.pause();
      blurVideoRef.current.srcObject = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }

  const fetchParcelInfosRef = useRef(null);

  const scanFrameRef = useRef(null);
  scanFrameRef.current = function scanFrame() {
    if (!scanningRef.current || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video.readyState >= video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(imageData.data, imageData.width, imageData.height);
      if (result) {
        const parcelID = extractParcelID(result.data);
        if (parcelID && !isFetchingRef.current) {
          isFetchingRef.current = true;
          freezeCamera();
          fetchParcelInfosRef.current(parcelID);
          return;
        }
      }
    }
    requestAnimationFrame(scanFrameRef.current);
  };

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      parcelScanPageDispatch({
        type: 'PAGE_PARCEL_SCAN_STATES_UPDATE',
        payload: { fetchAlert: true, alertMessage: "Caméra non disponible (connexion non sécurisée). Saisissez le code manuellement." },
      });
      setManual(true);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        streamRef.current = stream;
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          scanningRef.current = true;
          requestAnimationFrame(scanFrameRef.current);
        };
        if (blurVideoRef.current) {
          blurVideoRef.current.srcObject = stream;
          blurVideoRef.current.play().catch(() => {});
        }
      })
      .catch(() => {
        parcelScanPageDispatch({
          type: 'PAGE_PARCEL_SCAN_STATES_UPDATE',
          payload: { fetchAlert: true, alertMessage: "Impossible d'accéder à la caméra. Saisissez le code manuellement." },
        });
        setManual(true);
      });
    return () => stopCamera();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchParcelInfos = useCallback(async (parcelID) => {
    try {
      setLoading(true);
      const response = await getParcelInfo(parcelID);
      if (response.status !== 200) {
        throw new Error(`Error while fetching parcel info. Code: ${response.status}`);
      }
      const data = response.results;
      const brandData = buildBrandFromApiData(data);
      const parcelData = buildParcelFromApiData(data);
      consumerDispatch({ type: 'CONSUMER_BRAND_LOADED', brand: brandData });
      consumerDispatch({ type: 'CONSUMER_PARCEL_LOADED', parcel: parcelData });
      isFetchingRef.current = false;

      const actionType = searchParams.get('actionType');
      setLoading(false);

      if (actionType === 'returnOrder') {
        navigate('/sendback');
      } else {
        navigate('/deposit');
      }
    } catch (error) {
      parcelScanPageDispatch({ type: 'PAGE_PARCEL_SCAN_STATES_UPDATE', payload: { fetchAlert: true, alertMessage: "Code erroné" } });
      console.error("Error while fetching parcel info data:", error);
      isFetchingRef.current = false;
      setLoading(false);
      setScanError(true);
    }
  }, [consumerDispatch, parcelScanPageDispatch, searchParams, navigate]);

  fetchParcelInfosRef.current = fetchParcelInfos;

  function retryScan() {
    setScanError(false);
    isFetchingRef.current = false;
    handleAlertClose();
    resumeCamera();
    scanningRef.current = true;
    requestAnimationFrame(scanFrameRef.current);
  }

  function activateCamera() {
    setManual(false);
    setScanError(false);
    isFetchingRef.current = false;
    setCode('');
    handleAlertClose();
    resumeCamera();
    scanningRef.current = true;
    requestAnimationFrame(scanFrameRef.current);
  }

  function submitManual(e) {
    e.preventDefault();
    if (code.length < 6) return;
    fetchParcelInfos(normalizeParcelTagCode(code));
  }

  return (
    <Page>
      <BlurVideo ref={blurVideoRef} playsInline muted aria-hidden="true" />
      <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <TopBar>
        <ConsumerBackButton
          onBack={() => navigate('/')}
          variant="dark"
          label={t('scan.title')}
          ariaLabel={t('common.back')}
        />
      </TopBar>

      <ViewfinderZone>
        <Viewfinder aria-hidden="true">
          <Video ref={videoRef} playsInline muted $hidden={scanError || manual} />
          <Bracket $pos="tl" />
          <Bracket $pos="tr" />
          <Bracket $pos="bl" />
          <Bracket $pos="br" />
          {!loading && !scanError && !manual && <ScanLine />}
          {loading && (
            <LoadingOverlay>
              <Spinner />
              <LoadingLabel>Vérification en cours...</LoadingLabel>
            </LoadingOverlay>
          )}
          {scanError && !manual && (
            <ErrorOverlay>
              <ErrorIcon>✕</ErrorIcon>
              <ErrorLabel>{t('scan.error-label')}</ErrorLabel>
              <RetryBtn type="button" onClick={retryScan}>
                {t('scan.retry-cta')}
              </RetryBtn>
            </ErrorOverlay>
          )}
          {scanError && manual && (
            <ErrorOverlay>
              <RetryBtn type="button" onClick={activateCamera}>
                {t('scan.camera-activate-cta')}
              </RetryBtn>
            </ErrorOverlay>
          )}
        </Viewfinder>

        <Instruction>{t('scan.instruction-primary')}</Instruction>
        <SubInstruction>{t('scan.instruction-secondary')}</SubInstruction>
      </ViewfinderZone>

      <BottomZone>
        {!manual ? (
          <ManualBtn type="button" onClick={() => setManual(true)}>
            {t('scan.manual-cta')}
          </ManualBtn>
        ) : (
          <ManualForm onSubmit={submitManual}>
            <ManualInput
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder={t('scan.manual-placeholder')}
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
            />
            <SubmitBtn type="submit" disabled={code.length < 6 || loading}>
              {t('scan.manual-submit')}
            </SubmitBtn>
          </ManualForm>
        )}
      </BottomZone>
    </Page>
  );
}

const scanAnim = keyframes`
  0%   { transform: translateY(0); opacity: 0.4; }
  50%  { opacity: 1; }
  100% { transform: translateY(240px); opacity: 0.4; }
`;

const spinAnim = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #111;
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  color: #FFFFFF;
  padding: 0 16px 24px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
`;

const BlurVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(6px) brightness(0.55);
  z-index: 0;
  pointer-events: none;
`;

const TopBar = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const ViewfinderZone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const Viewfinder = styled.div`
  position: relative;
  width: 260px;
  height: 260px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${p => p.$hidden && 'display: none;'}
`;

const ErrorOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 10, 0.93);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 2;
`;

const ErrorIcon = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.theme.palette.error.main};
  line-height: 1;
`;

const ErrorLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 10, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 2;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: ${p => p.theme.palette.accent.main};
  border-radius: 50%;
  animation: ${spinAnim} 0.8s linear infinite;
`;

const LoadingLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
`;

const Bracket = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  border-color: ${p => p.theme.palette.accent.main};
  border-style: solid;
  z-index: 1;
  ${p => p.$pos === 'tl' && 'top: -3px; left: -3px; border-width: 3px 0 0 3px;'}
  ${p => p.$pos === 'tr' && 'top: -3px; right: -3px; border-width: 3px 3px 0 0;'}
  ${p => p.$pos === 'bl' && 'bottom: -3px; left: -3px; border-width: 0 0 3px 3px;'}
  ${p => p.$pos === 'br' && 'bottom: -3px; right: -3px; border-width: 0 3px 3px 0;'}
`;

const ScanLine = styled.div`
  position: absolute;
  left: 8px;
  right: 8px;
  height: 2px;
  background: ${p => p.theme.palette.accent.main};
  box-shadow: 0 0 12px ${p => p.theme.palette.accent.main};
  z-index: 1;
  animation: ${scanAnim} 2.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    top: 50%;
  }
`;

const Instruction = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #FFFFFF;
  text-align: center;
  letter-spacing: -0.3px;
`;

const SubInstruction = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  text-align: center;
  margin-top: -10px;
`;

const BottomZone = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
  position: relative;
  z-index: 1;
`;

const RetryBtn = styled.button`
  padding: 10px 22px;
  border-radius: 8px;
  background: ${p => p.theme.palette.accent.main};
  border: none;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  margin-top: 4px;
`;

const ManualBtn = styled.button`
  padding: 13px 20px;
  border-radius: 10px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.75);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
`;

const ManualForm = styled.form`
  display: flex;
  gap: 8px;
`;

const ManualInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  color: #FFFFFF;
  font-size: 14px;
  outline: none;
  font-family: ${p => p.theme.typography.monorama.fontFamily};
  font-weight: 600;
  letter-spacing: 1px;
  &:focus-visible {
    border-color: ${p => p.theme.palette.accent.main};
  }
`;

const SubmitBtn = styled.button`
  padding: 12px 18px;
  border-radius: 10px;
  background: ${p => p.theme.palette.accent.main};
  border: none;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  box-shadow: ${p => p.theme.shadows[2]};
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
